'use strict';

// ---------------------------------------------------------------------------
// DOM references
// ---------------------------------------------------------------------------
const startScreen = document.getElementById('start-screen');
const nicknameInput = document.getElementById('nickname');
const playBtn = document.getElementById('play-btn');
const connStatus = document.getElementById('conn-status');

const canvas = document.getElementById('game-canvas');
const ctx = canvas.getContext('2d');
const hud = document.getElementById('hud');
const minimap = document.getElementById('minimap');
const minimapCtx = minimap.getContext('2d');
const lbList = document.getElementById('lb-list');
const statScore = document.getElementById('stat-score');
const cooldownRing = document.getElementById('cooldown-ring');
const ringFg = document.getElementById('ring-fg');

const muteBtn = document.getElementById('mute-btn');

const deathScreen = document.getElementById('death-screen');
const deathReasonEl = document.getElementById('death-reason');
const deathShotsEl = document.getElementById('death-shots');
const deathKillsEl = document.getElementById('death-kills');

const hofPreview = document.getElementById('hof-preview');
const hofList = document.getElementById('hof-list');
const hofBtn = document.getElementById('hof-btn');
const hofBackBtn = document.getElementById('hof-back-btn');

function describeDeath(reason, killerName) {
  if (reason === 'shot') return `Destroyed by ${killerName || 'enemy fire'}`;
  if (reason === 'collision') return 'Destroyed in a collision';
  if (reason === 'wall') return 'Drove off the edge';
  return 'Game Over';
}

const lastKillsEl = document.getElementById('last-kills');
const lastShotsEl = document.getElementById('last-shots');
const bestScoreEl = document.getElementById('best-score');
const bestShotsEl = document.getElementById('best-shots');

// ---------------------------------------------------------------------------
// Sound
// ---------------------------------------------------------------------------
// Loading the .wav files (fetch + decode) and getting the first user
// gesture (required before Web Audio is allowed to make sound) happen
// independently and can finish in either order. Only start the menu loop
// once BOTH have happened, whichever finishes last triggers it - otherwise
// a fast first click can race ahead of the still-loading buffer and the
// (one-shot) gesture is wasted with no retry, leaving the menu silent.
let audioBuffersLoaded = false;
let audioGestureUnlocked = false;
function maybeStartMenuLoop() {
  if (audioBuffersLoaded && audioGestureUnlocked && !gameActive) {
    SoundManager.playLoop('menuLoop', { volume: 0.35 });
  }
}
// Only the menu loop is needed to unmute the start screen - load it first so
// that doesn't wait on the other 6 gameplay sounds (shoot/impact/kill/etc.),
// which aren't needed until actual gameplay events start happening and load
// afterward in the background instead.
SoundManager.loadKeys(['menuLoop']).then(() => {
  audioBuffersLoaded = true;
  maybeStartMenuLoop();
  const rest = Object.keys(SoundManager.MANIFEST).filter((k) => k !== 'menuLoop');
  SoundManager.loadKeys(rest);
});

function updateMuteBtn() {
  muteBtn.classList.toggle('is-active', SoundManager.isMuted());
}
updateMuteBtn();
muteBtn.addEventListener('click', () => {
  SoundManager.toggleMuted();
  updateMuteBtn();
});

// ---------------------------------------------------------------------------
// Fullscreen toggle
// ---------------------------------------------------------------------------
const fullscreenBtn = document.getElementById('fullscreen-btn');
const fullscreenSupported = !!(
  document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen
);
if (!fullscreenSupported) {
  fullscreenBtn.style.display = 'none';
} else {
  const isFullscreen = () => !!(document.fullscreenElement || document.webkitFullscreenElement);
  const updateFullscreenBtn = () => {
    const fs = isFullscreen();
    fullscreenBtn.classList.toggle('is-active', fs);
    fullscreenBtn.setAttribute('aria-label', fs ? 'Exit fullscreen' : 'Fullscreen');
    fullscreenBtn.title = fs ? 'Exit fullscreen' : 'Fullscreen';
  };
  updateFullscreenBtn();
  fullscreenBtn.addEventListener('click', () => {
    if (isFullscreen()) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    }
  });
  document.addEventListener('fullscreenchange', updateFullscreenBtn);
  document.addEventListener('webkitfullscreenchange', updateFullscreenBtn);
}

// ---------------------------------------------------------------------------
// Dark / light theme toggle
// ---------------------------------------------------------------------------
// Colors the canvas itself draws (CSS alone can't reach into <canvas>
// pixels) - kept in sync with the CSS theme via currentTheme below.
const THEME_COLORS = {
  dark: { canvasBg: '#0e1526', hexStroke: 'rgba(255,255,255,0.09)' },
  light: { canvasBg: '#dbe3f0', hexStroke: 'rgba(20,30,60,0.13)' },
};
const THEME_STORAGE_KEY = 'panzer_theme';
const themeBtn = document.getElementById('theme-btn');
const prefersLightQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: light)') : null;
let currentTheme = 'dark';

function applyTheme(theme) {
  currentTheme = theme;
  document.documentElement.setAttribute('data-theme', theme);
  themeBtn.classList.toggle('is-active', theme === 'light');
  const row = document.getElementById('theme-toggle-row');
  if (row) row.setAttribute('aria-checked', String(theme === 'light'));
}

let savedTheme = null;
try { savedTheme = localStorage.getItem(THEME_STORAGE_KEY); } catch (e) {}
applyTheme(savedTheme || (prefersLightQuery && prefersLightQuery.matches ? 'light' : 'dark'));

themeBtn.addEventListener('click', () => {
  const next = currentTheme === 'light' ? 'dark' : 'light';
  applyTheme(next);
  try { localStorage.setItem(THEME_STORAGE_KEY, next); } catch (e) {}
});

// Follow live OS theme changes, but only until the user picks one explicitly
// themselves - an explicit choice in localStorage always wins from then on.
if (prefersLightQuery) {
  const handleSystemThemeChange = (e) => {
    let hasExplicitChoice = false;
    try { hasExplicitChoice = !!localStorage.getItem(THEME_STORAGE_KEY); } catch (err) {}
    if (!hasExplicitChoice) applyTheme(e.matches ? 'light' : 'dark');
  };
  if (prefersLightQuery.addEventListener) prefersLightQuery.addEventListener('change', handleSystemThemeChange);
  else if (prefersLightQuery.addListener) prefersLightQuery.addListener(handleSystemThemeChange);
}

function unlockAudioOnce() {
  window.removeEventListener('pointerdown', unlockAudioOnce);
  window.removeEventListener('keydown', unlockAudioOnce);
  SoundManager.unlock();
  audioGestureUnlocked = true;
  maybeStartMenuLoop();
}
window.addEventListener('pointerdown', unlockAudioOnce, { once: true });
window.addEventListener('keydown', unlockAudioOnce, { once: true });

// ---------------------------------------------------------------------------
// PWA installability (manifest.webmanifest is linked from index.html) - the
// service worker's only job is satisfying the install criteria, see sw.js.
// ---------------------------------------------------------------------------
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').catch(() => {});
  });
}

// ---------------------------------------------------------------------------
// Pointer capability detection - desktop turns with A/D (or arrow keys) and
// aims/fires with the mouse; touch steers by dragging and fires on a
// double-tap (full input wiring is further down, near "Input:"). Both sets
// of listeners are always live regardless of device, so a mouse and a
// touchscreen can be used interchangeably (even at the same time on a
// hybrid device) - no scheme to pick, so nothing to get wrong.
//
// This detection only feeds the on-screen controls hint text below: hybrid
// devices (a touchscreen laptop, a Surface, an iPad with a trackpad) match
// both `any-pointer: fine` and `any-pointer: coarse` - unlike `pointer`,
// which only reflects the *primary* input, `any-pointer` checks every
// available input mechanism - so they get both hints shown at once; a
// non-hybrid device gets just the one hint matching its input.
// ---------------------------------------------------------------------------
const hasCoarsePointer = (window.matchMedia && window.matchMedia('(any-pointer: coarse)').matches)
  || navigator.maxTouchPoints > 0
  || 'ontouchstart' in window;
const hasFinePointer = !!(window.matchMedia && window.matchMedia('(any-pointer: fine)').matches);
const isHybridDevice = hasCoarsePointer && hasFinePointer;

// Which input actually drove the last aim update - mouse aims continuously,
// while touch only deviates from the hull heading to hold the exact aim of
// the last shot while frozen (see the drawTank call in draw()). Tracked
// live (rather than picked once from device capabilities) so a hybrid
// device gets the right rendering for whichever input it's using right now.
let lastAimInput = hasCoarsePointer && !hasFinePointer ? 'touch' : 'mouse';

// ---------------------------------------------------------------------------
// Settings panel (menu-only sub-screen): tank color, SFX/music, theme.
// ---------------------------------------------------------------------------
// Mirrors server COLORS exactly (server/index.js) - duplicated here so the
// color swatches can render before a socket ever connects, rather than
// waiting on the 'colors' message.
const TANK_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f1c40f', '#9b59b6',
  '#e67e22', '#1abc9c', '#ff6fa5', '#00d2ff', '#c0392b',
];
const STORAGE_TANK_COLOR = 'panzer_tank_color';

// null = no explicit preference -> server assigns a random color (matches
// the original default behavior for anyone who never opens Settings).
let selectedTankColor = loadJSON(STORAGE_TANK_COLOR, null);

// TANK_COLORS index for a hex string, for TankSprites.drawTankSprite()
// (public/tank-sprites.js), which picks a baked sprite frame by palette
// index rather than by raw hex. Every real in-game color always comes from
// this exact palette (roster entries are resolved from cfg.colors, itself
// a mirror of server COLORS - see the 'roster' handler below), so this is
// normally an exact match; the nearest-RGB fallback only ever fires for the
// placeholder colors drawTank() itself passes before roster/join data has
// arrived ('#888888', '#3ddc55') or a stale/out-of-range saved preference.
const colorIndexCache = new Map();
function colorToIndex(hex) {
  const key = String(hex).toLowerCase();
  let idx = colorIndexCache.get(key);
  if (idx !== undefined) return idx;
  idx = TANK_COLORS.indexOf(key);
  if (idx === -1) idx = nearestTankColorIndex(key);
  colorIndexCache.set(key, idx);
  return idx;
}
function nearestTankColorIndex(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < TANK_COLORS.length; i++) {
    const c = hexToRgb(TANK_COLORS[i]);
    const dist = (c.r - rgb.r) ** 2 + (c.g - rgb.g) ** 2 + (c.b - rgb.b) ** 2;
    if (dist < bestDist) { bestDist = dist; best = i; }
  }
  return best;
}
function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

const settingsBtn = document.getElementById('settings-btn');
const settingsBackBtn = document.getElementById('settings-back-btn');
const studioBtn = document.getElementById('studio-btn');
const studioBackBtn = document.getElementById('studio-back-btn');
const openStudioRowBtn = document.getElementById('open-studio-row-btn');

// Settings and Hall of Fame are mutually exclusive menu sub-screens (see the
// shared .settings-open/.hof-open rules in style.css) - opening one always
// closes the other, and Escape closes whichever is open.
function closeSubscreens() {
  startScreen.classList.remove('settings-open', 'hof-open', 'studio-open');
}
function openSubscreen(name) {
  closeSubscreens();
  startScreen.classList.add(name);
}
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeSubscreens();
});

settingsBtn.addEventListener('click', () => {
  if (startScreen.classList.contains('settings-open')) closeSubscreens();
  else openSubscreen('settings-open');
});
settingsBackBtn.addEventListener('click', closeSubscreens);

hofBtn.addEventListener('click', () => {
  if (startScreen.classList.contains('hof-open')) {
    closeSubscreens();
    return;
  }
  openSubscreen('hof-open');
  renderHofList();
  fetchHallOfFame(); // refresh in the background in case it's gone stale
});
hofBackBtn.addEventListener('click', closeSubscreens);

studioBtn.addEventListener('click', () => {
  if (startScreen.classList.contains('studio-open')) closeSubscreens();
  else openSubscreen('studio-open');
});
studioBackBtn.addEventListener('click', closeSubscreens);
openStudioRowBtn.addEventListener('click', () => openSubscreen('studio-open'));

function initToggleSwitch(btn, getInitial, onToggle) {
  btn.setAttribute('aria-checked', String(getInitial()));
  btn.addEventListener('click', () => {
    const next = btn.getAttribute('aria-checked') !== 'true';
    btn.setAttribute('aria-checked', String(next));
    onToggle(next);
  });
}

initToggleSwitch(
  document.getElementById('sfx-toggle'),
  () => SoundManager.isSfxEnabled(),
  (on) => SoundManager.setSfxEnabled(on)
);
initToggleSwitch(
  document.getElementById('music-toggle'),
  () => SoundManager.isMusicEnabled(),
  (on) => SoundManager.setMusicEnabled(on)
);
initToggleSwitch(
  document.getElementById('theme-toggle-row'),
  () => currentTheme === 'light',
  (on) => {
    const next = on ? 'light' : 'dark';
    applyTheme(next);
    try { localStorage.setItem(THEME_STORAGE_KEY, next); } catch (e) {}
  }
);

function buildColorSwatches() {
  const container = document.getElementById('color-swatches');

  const randomBtn = document.createElement('button');
  randomBtn.className = 'color-swatch random';
  randomBtn.setAttribute('aria-label', 'Random tank color');
  randomBtn.title = 'Random';
  randomBtn.innerHTML = '<svg class="icon shuffle-badge"><use href="#icon-shuffle"/></svg>';
  randomBtn.addEventListener('click', () => selectTankColor(null));
  container.appendChild(randomBtn);

  TANK_COLORS.forEach((hex, i) => {
    const btn = document.createElement('button');
    btn.className = 'color-swatch';
    btn.style.setProperty('--swatch-color', hex);
    btn.setAttribute('aria-label', `Tank color ${i + 1}`);
    btn.innerHTML = '<svg class="icon check-badge"><use href="#icon-check"/></svg>';
    btn.addEventListener('click', () => selectTankColor(i));
    container.appendChild(btn);
  });

  refreshColorSwatchSelection();
}

function refreshColorSwatchSelection() {
  const container = document.getElementById('color-swatches');
  const children = [...container.children];
  children.forEach((el, idx) => {
    // idx 0 is the random swatch (no color index), idx-1 maps to TANK_COLORS.
    const isSelected = idx === 0 ? selectedTankColor === null : (idx - 1) === selectedTankColor;
    el.classList.toggle('selected', isSelected);
  });
}

function selectTankColor(index) {
  selectedTankColor = index;
  saveJSON(STORAGE_TANK_COLOR, index);
  refreshColorSwatchSelection();
  // Lets the fully-decoupled tank-studio.js module (an ES module, unlike this
  // classic script) recolor its live 3D preview without either file
  // importing the other - see public/tank-studio.js.
  window.dispatchEvent(new CustomEvent('tanks:colorchange', { detail: { index } }));
}

buildColorSwatches();

// ---------------------------------------------------------------------------
// Hall of Fame: top-100 all-time scores. Served from GET /api/hall-of-fame
// rather than pushed over the game socket - it needs to be visible on the
// start screen before a player has ever connected, and is read-mostly, so a
// plain fetch (on load, whenever the panel opens, and after returning to
// the menu) is simpler than keeping it live-synced.
// ---------------------------------------------------------------------------
let hallOfFame = [];

async function fetchHallOfFame() {
  try {
    const res = await fetch('/api/hall-of-fame');
    hallOfFame = await res.json();
  } catch (e) {
    hallOfFame = [];
  }
  renderHofPreview();
  if (startScreen.classList.contains('hof-open')) renderHofList();
}

function renderHofPreview() {
  const top3 = hallOfFame.slice(0, 3);
  hofPreview.classList.toggle('hidden', top3.length === 0);
  hofPreview.innerHTML = '';
  if (!top3.length) return;

  const MEDALS = ['gold', 'silver', 'bronze'];
  top3.forEach((entry, i) => {
    const row = document.createElement('div');
    row.className = 'hof-preview-row';
    const rank = document.createElement('span');
    rank.className = 'hof-preview-rank';
    rank.textContent = `${i + 1}.`;
    const name = document.createElement('span');
    name.className = 'hof-preview-name';
    name.textContent = entry.name;
    const score = document.createElement('span');
    // MEDALS[i] (a fixed rank class, never user data) also drives the pill's
    // gold/silver/bronze background - see .hof-score-badge.gold etc in
    // style.css.
    score.className = `hof-preview-score hof-score-badge ${MEDALS[i]}`;
    score.innerHTML = '<svg class="icon hof-preview-medal"><use href="#icon-trophy"/></svg>';
    score.append(document.createTextNode(entry.score));
    row.append(rank, name, score);
    hofPreview.appendChild(row);
  });
}

function renderHofList() {
  hofList.innerHTML = '';
  if (!hallOfFame.length) {
    const li = document.createElement('li');
    li.className = 'hof-empty';
    li.textContent = 'No scores yet - be the first!';
    hofList.appendChild(li);
    return;
  }
  const MEDALS = ['gold', 'silver', 'bronze'];
  hallOfFame.forEach((entry, i) => {
    const li = document.createElement('li');
    const rank = document.createElement('span');
    rank.className = 'hof-rank';
    rank.textContent = `${i + 1}.`;
    const name = document.createElement('span');
    name.className = 'hof-name';
    name.textContent = entry.name;
    const score = document.createElement('span');
    score.className = 'hof-score hof-score-badge';
    if (i < 3) {
      // MEDALS[i] (a fixed rank class, never user data) also drives the
      // pill's gold/silver/bronze background - see .hof-score-badge.gold
      // etc in style.css.
      score.classList.add(MEDALS[i]);
      score.innerHTML = '<svg class="icon hof-medal"><use href="#icon-trophy"/></svg>';
    }
    score.append(document.createTextNode(entry.score));
    li.append(rank, name, score);
    hofList.appendChild(li);
  });
}

fetchHallOfFame();

// ---------------------------------------------------------------------------
// Config (server sends authoritative values on 'welcome', these are fallbacks)
// ---------------------------------------------------------------------------
const cfg = {
  worldSize: 4000,
  tankRadius: 24,
  blastRadius: 34,
  shotStopMs: 250,
  turnRate: 3.2,
  speed: 112.5,
  colors: [],
};

// Zoom-out factor for rendering: <1 shows more of the world on screen.
// Mutable (and eased toward `zoomTarget` each frame in updateLocalPrediction)
// so the death-screen zoom-out is real canvas content - drawn by the hex
// grid / world-bounds code like everything else - rather than a CSS
// transform on the canvas element, which would just reveal blank page
// background at the edges instead of more of the actual game.
//
// "Fair" zoom: a wider viewport otherwise shows strictly more of the world
// for free (more incoming tanks/shots visible, sooner) - a real competitive
// edge in a multiplayer .io game. computeBaseZoom() counteracts that by
// scaling zoom with the viewport diagonal, anchored to a typical phone
// viewport: every screen then sees roughly the same *amount* of world, just
// rendered at a size that matches its own diagonal. The clamp only guards
// pathological viewport sizes (tiny embedded webviews) - normal phones
// through ultrawide monitors land well inside it.
const ZOOM_REFERENCE_DIAGONAL = Math.hypot(390, 844); // ~a typical phone viewport
// 0.6 originally, x1.75 so the (now sprite-rendered) tank reads bigger - see
// wild-percolating-breeze.md. Scales the whole view uniformly (world, other
// tanks, projectiles), not just the tank, since it's the one shared knob.
const ZOOM_AT_REFERENCE = .7;
function computeBaseZoom() {
  const diagonal = Math.hypot(window.innerWidth, window.innerHeight);
  return clamp(ZOOM_AT_REFERENCE * (diagonal / ZOOM_REFERENCE_DIAGONAL), 0.35, 3);
}

let BASE_ZOOM = computeBaseZoom();
let ZOOM = BASE_ZOOM;
let zoomTarget = BASE_ZOOM;
// 1 normally, 0.88 during the post-death zoom-out - kept separate from
// BASE_ZOOM so a resize mid-death (rotation, window drag) recomputes the
// fair zoom without losing the death effect (see resizeCanvas below).
let zoomTargetMultiplier = 1;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
let ws = null;
let myId = null;
// True right after a 'full' rejection, until the next connect attempt - lets
// ws.onclose (which fires moments later, since the server closes the socket
// after refusing the join) skip clobbering the "server is full" message with
// its generic "Disconnected." text.
let joinRejected = false;
let gameActive = false;
// True from the moment of death until returnToMenu() actually fires (i.e.
// through the whole death-screen spectate window) - world sound effects stay
// audible during that window and only cut out once we're really at the menu.
let postDeathActive = false;
let myName = '';

const localTank = { x: 0, y: 0, angle: 0, targetAngle: 0, turretAngle: 0, stoppedUntil: 0, alive: true };
const camera = { x: 0, y: 0 };

let latestState = { players: [], projectiles: [], t: Date.now() };
// id -> {name, color}. Populated from 'roster' messages - name/color are
// static per player, so the server sends them once (not in every 30Hz
// 'state' tick) and this is where the client keeps them.
const roster = new Map();
let latestRecvAt = Date.now();
let serverOffset = 0;
let lastKnownScore = 0;
const knownProjectileIds = new Set();

const explosions = []; // {x, y, startedAt, big}
const targetMarkers = []; // {x, y, startedAt} - local-only "you fired here" ping

const pointer = { x: 0, y: 0 };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function normalizeAngle(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
function lerp(a, b, t) { return a + (b - a) * t; }
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// Half-diagonal of the current viewport in world units - i.e. the distance
// from the camera to the farthest corner still on screen. Used as the
// hearing range so audio roughly matches what's actually visible, and
// automatically adapts to window size / device and the camera zoom.
function visibleRadius() {
  return Math.hypot(cw / 2, ch / 2) / ZOOM;
}

// Volume for a sound happening at a world position, relative to the local
// tank: full volume up close, fading out (faster than linear, so mid-range
// sounds are noticeably quieter) to true silence at maxDist.
function distanceVolume(wx, wy, maxDist, peakVol = 1) {
  const dist = Math.hypot(wx - localTank.x, wy - localTank.y);
  if (dist >= maxDist) return 0;
  const t = 1 - dist / maxDist;
  return peakVol * t * t;
}

// ---------------------------------------------------------------------------
// Canvas sizing
// ---------------------------------------------------------------------------
let cw = window.innerWidth;
let ch = window.innerHeight;

function resizeCanvas() {
  // Cap DPR like the menu-tank three.js canvas does (menu-tank.js) - above 2x
  // the backing store grows (and everything drawn into it costs more) with no
  // visible sharpening benefit, which matters most on high-DPR phones.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  cw = window.innerWidth;
  ch = window.innerHeight;
  canvas.width = cw * dpr;
  canvas.height = ch * dpr;
  canvas.style.width = cw + 'px';
  canvas.style.height = ch + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Re-derive the fair zoom for the new viewport (orientation change, window
  // resize/drag, entering fullscreen, ...) and retarget without snapping, so
  // it eases into place like any other zoom change instead of jumping.
  BASE_ZOOM = computeBaseZoom();
  zoomTarget = BASE_ZOOM * zoomTargetMultiplier;
}
window.addEventListener('resize', resizeCanvas);
resizeCanvas();

// ---------------------------------------------------------------------------
// Networking
// ---------------------------------------------------------------------------
// Connection-lost states (dropped mid-game, or the initial handshake itself
// failing) leave the player stuck unless they know they can retry - spell
// that out with an inline button instead. Reconnect only re-establishes the
// socket (autoJoin: false) rather than reusing connectAndJoin() outright, so
// clicking it lands the player back on the menu with a working connection
// instead of dropping them straight into a fresh game before they've chosen
// to play again.
function showConnMessage(text, offerReconnect) {
  connStatus.textContent = text;
  if (!offerReconnect) return;
  connStatus.appendChild(document.createTextNode(' '));
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'reconnect-btn';
  btn.textContent = 'Reconnect';
  btn.addEventListener('click', () => connect(myName, false));
  connStatus.appendChild(btn);
}

// Shown on the start screen when the server rejected a join (or a fresh
// connection) because a deploy is draining - reloading picks up the new
// client build alongside the new server, rather than reconnecting into a
// world that's about to restart. See beginDrain in server/index.js.
function showUpdateAvailable() {
  connStatus.textContent = 'Panzer.li just got an update.';
  connStatus.appendChild(document.createTextNode(' '));
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'reconnect-btn';
  btn.textContent = 'Reload';
  btn.addEventListener('click', () => location.reload());
  connStatus.appendChild(btn);
}

function connectAndJoin(name) {
  connect(name, true);
}

function connect(name, autoJoin) {
  if (ws) {
    ws.onclose = null;
    ws.onerror = null;
    try { ws.close(); } catch (e) {}
  }

  // No "Connecting..." placeholder here - the Play button already shows its
  // own spinner (see setPlayButtonLoading), and on a fast/local connection
  // this text would otherwise just flash for a frame before onopen clears it.
  connStatus.textContent = '';
  joinRejected = false;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  ws = new WebSocket(`${proto}://${location.host}`);

  ws.onopen = () => {
    connStatus.textContent = '';
    if (autoJoin) ws.send(JSON.stringify({ type: 'join', name, color: selectedTankColor }));
  };

  ws.onmessage = (ev) => {
    let msg;
    try { msg = JSON.parse(ev.data); } catch { return; }

    if (msg.type === 'colors') {
      cfg.colors = msg.colors;
      return;
    }

    if (msg.type === 'full') {
      joinRejected = true;
      setPlayButtonLoading(false);
      connStatus.textContent = 'Server is full - try again in a moment.';
      return;
    }

    if (msg.type === 'update-required') {
      joinRejected = true;
      setPlayButtonLoading(false);
      showUpdateAvailable();
      return;
    }

    if (msg.type === 'roster') {
      for (const p of msg.players) {
        roster.set(p.id, { name: p.name, color: cfg.colors[p.color] || '#888' });
      }
      return;
    }

    if (msg.type === 'welcome') {
      myId = msg.id;
      cfg.worldSize = msg.worldSize;
      cfg.tankRadius = msg.tankRadius;
      cfg.blastRadius = msg.blastRadius;
      cfg.shotStopMs = msg.shotStopMs;
      cfg.turnRate = msg.turnRate;
      cfg.speed = msg.speed;

      localTank.x = msg.x;
      localTank.y = msg.y;
      localTank.angle = msg.angle;
      localTank.targetAngle = msg.angle;
      localTank.turretAngle = msg.angle;
      localTank.stoppedUntil = 0;
      localTank.alive = true;
      camera.x = msg.x;
      camera.y = msg.y;
      lastKnownScore = 0;
      knownProjectileIds.clear();

      gameActive = true;
      postDeathActive = false;
      setPlayButtonLoading(false);
      startScreen.classList.add('screen-off');
      deathScreen.classList.add('screen-off');
      hud.classList.remove('screen-off');
      zoomTargetMultiplier = 1;
      ZOOM = BASE_ZOOM;
      zoomTarget = BASE_ZOOM;
      SoundManager.stopLoop('menuLoop');
      SoundManager.playLoop('gameLoop', { volume: 0.28 });
      return;
    }

    if (msg.type === 'state') {
      latestState = msg;
      latestRecvAt = Date.now();
      serverOffset = msg.t - latestRecvAt;

      for (const e of msg.explosions) {
        explosions.push({ x: e.x, y: e.y, startedAt: latestRecvAt, big: !!e.big });
        // Sound effects for world events (impact/shoot/kill below) stay
        // audible through the post-death spectate window, same as the
        // rendering does - they only cut out once we're actually back at
        // the menu (postDeathActive goes false in returnToMenu()).
        if (gameActive || postDeathActive) {
          const vol = distanceVolume(e.x, e.y, visibleRadius());
          if (vol > 0) SoundManager.play('impact', { volume: vol });
        }
      }

      // New shots fired by OTHER tanks: play at a volume based on how far
      // away the shooter is. Our own shot already played instantly (with no
      // network round-trip) back in trySendShoot, so it's skipped here.
      const seenIds = new Set();
      for (const pr of msg.projectiles) {
        seenIds.add(pr.id);
        if ((gameActive || postDeathActive) && !knownProjectileIds.has(pr.id) && pr.ownerId !== myId) {
          const vol = distanceVolume(pr.sx, pr.sy, visibleRadius());
          if (vol > 0) SoundManager.play('shoot', { volume: vol * 0.8 });
        }
      }
      knownProjectileIds.clear();
      for (const id of seenIds) knownProjectileIds.add(id);

      const me = msg.players.find((p) => p.id === myId);
      if (me && gameActive) {
        if (me.score > lastKnownScore) SoundManager.play('kill', { volume: 0.75 });
        lastKnownScore = me.score;

        const dx = me.x - localTank.x;
        const dy = me.y - localTank.y;
        if (Math.hypot(dx, dy) > 220) {
          localTank.x = me.x;
          localTank.y = me.y;
        } else {
          localTank.x += dx * 0.25;
          localTank.y += dy * 0.25;
        }
      }

      // The leaderboard only needs to reflect what's in latestState, which
      // only changes when a new state message arrives (30Hz) - drawing it
      // from the 60fps render loop instead was pure wasted DOM churn.
      if (gameActive || hud.classList.contains('screen-off') === false) {
        drawLeaderboard();
      }
      return;
    }

    if (msg.type === 'dead') {
      gameActive = false;
      postDeathActive = true;
      SoundManager.slowDownAndStop('gameLoop', 1.3);
      SoundManager.play('gameOver', { volume: 0.85 });
      hud.classList.add('screen-off');
      deathScreen.classList.remove('screen-off');
      deathReasonEl.textContent = describeDeath(msg.reason, msg.killerName);
      deathShotsEl.textContent = msg.shots;
      deathKillsEl.textContent = msg.kills;
      recordRunStats(msg.score, msg.kills, msg.shots);
      if (msg.updateAvailable) {
        // Server is draining for a deploy: skip the usual auto-return (it'd
        // just get bounced by 'update-required' anyway) and prompt a reload
        // instead, right here on the death screen the player is looking at.
        const notice = document.createElement('div');
        notice.className = 'death-update-notice';
        notice.textContent = 'Panzer.li just got an update - ';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'reconnect-btn';
        btn.textContent = 'Reload';
        btn.addEventListener('click', () => location.reload());
        notice.appendChild(btn);
        deathReasonEl.appendChild(document.createElement('br'));
        deathReasonEl.appendChild(notice);
      } else {
        startDeathCountdown();
      }
      return;
    }
  };

  ws.onclose = (ev) => {
    setPlayButtonLoading(false);
    if (gameActive) {
      gameActive = false;
      SoundManager.stopLoop('gameLoop');
      SoundManager.playLoop('menuLoop', { volume: 0.35 });
      hud.classList.add('screen-off');
      deathScreen.classList.add('screen-off');
      startScreen.classList.remove('screen-off');
    }
    // 4010: server is draining for a deploy (see beginDrain in
    // server/index.js) - already told via 'update-required', don't clobber
    // that message with the generic "Disconnected." text.
    if (ev.code === 4010) return;
    if (!joinRejected) showConnMessage('Disconnected.', true);
  };

  ws.onerror = () => {
    setPlayButtonLoading(false);
    showConnMessage('Connection failed.', true);
  };
}

function sendInput() {
  if (ws && ws.readyState === WebSocket.OPEN && gameActive) {
    ws.send(JSON.stringify({ type: 'input', angle: localTank.targetAngle }));
  }
}
setInterval(sendInput, 50);

function trySendShoot(worldX, worldY) {
  if (!gameActive || !ws || ws.readyState !== WebSocket.OPEN) return;
  const now = Date.now();
  if (now < localTank.stoppedUntil) return; // still frozen client-side
  localTank.stoppedUntil = now + cfg.shotStopMs;
  // Only the turret snaps to the target. localTank.targetAngle may already
  // have drifted toward the click (the pointer had to move there to click
  // it) - snap it back to the current facing so the hull resumes straight
  // after the freeze instead of turning toward the shot.
  localTank.turretAngle = Math.atan2(worldY - localTank.y, worldX - localTank.x);
  localTank.targetAngle = localTank.angle;
  SoundManager.play('shoot', { volume: 0.6 });
  targetMarkers.push({ x: worldX, y: worldY, startedAt: now });
  ws.send(JSON.stringify({ type: 'shoot', x: worldX, y: worldY }));
}

// ---------------------------------------------------------------------------
// Proximity sensor: beeps faster the closer the nearest enemy tank gets.
// ---------------------------------------------------------------------------
const PROXIMITY_RANGE = 180; // only trips when a tank is genuinely close
let nextProximityBeepAt = 0;

function updateProximitySensor() {
  if (!gameActive) return;
  let nearestDist = Infinity;
  for (const p of latestState.players) {
    if (p.id === myId) continue;
    const d = Math.hypot(p.x - localTank.x, p.y - localTank.y);
    if (d < nearestDist) nearestDist = d;
  }
  if (nearestDist >= PROXIMITY_RANGE) return;
  const now = Date.now();
  if (now < nextProximityBeepAt) return;
  const closeness = 1 - nearestDist / PROXIMITY_RANGE; // 0..1, 1 = right next to it
  nextProximityBeepAt = now + (450 - closeness * 350); // 450ms at edge -> 100ms touching
  SoundManager.play('proximity', { volume: 0.35 + closeness * 0.4 });
}
setInterval(updateProximitySensor, 80);

// ---------------------------------------------------------------------------
// Input: desktop turns with A/D (or the left/right arrows) and aims/fires
// with the mouse; touch steers by dragging and fires on a double-tap. Both
// sets of listeners are always attached and always active, so mouse/keyboard
// and touch can be used interchangeably - even at once on a hybrid device.
// (See #controls-hint in the DOM for the player-facing summary, and the
// pointer detection block above for how that hint is picked.)
// ---------------------------------------------------------------------------

// Which way the hull is currently rotating on desktop ('left'/'right', or
// unset if neither key is held) - read every frame in updateLocalPrediction
// to turn continuously for as long as the key stays down, rather than
// snapping to a fixed angle on keydown. Touch never populates this.
const pressedRotateDirs = new Set();

function screenToWorld(px, py) {
  return {
    x: camera.x + (px - cw / 2) / ZOOM,
    y: camera.y + (py - ch / 2) / ZOOM,
  };
}

function renderControlsHint() {
  const el = document.getElementById('controls-hint');
  if (!el) return;
  const touchHint = '<span class="hint-line">'
    + '<span class="hint-group"><svg class="icon"><use href="#icon-touch"/></svg>Drag to steer</span>'
    + '<span class="hint-group"><svg class="icon"><use href="#icon-touch"/></svg>Double-tap to fire</span>'
    + '</span>';
  const mouseHint = '<span class="hint-line">'
    + '<span class="hint-group"><svg class="icon"><use href="#icon-keyboard"/></svg>A/D or ←/→ to turn</span>'
    + '<span class="hint-group"><svg class="icon"><use href="#icon-mouse"/></svg>Mouse aims, click fires</span>'
    + '</span>';
  el.innerHTML = isHybridDevice ? (touchHint + mouseHint) : (hasCoarsePointer ? touchHint : mouseHint);
}
renderControlsHint();

{
  // --- Desktop: the tank always drives forward - A/D (or left/right arrow)
  // rotate the hull continuously for as long as they're held, the mouse
  // continuously aims the turret (independent of movement), left-click
  // fires toward the current aim point. Always live alongside the touch
  // listeners below, so a mouse/keyboard just works whenever it's used. ---
  const ROTATE_KEYS = { a: 'left', arrowleft: 'left', d: 'right', arrowright: 'right' };

  function isEditableTarget(el) {
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
  }

  window.addEventListener('keydown', (e) => {
    const dir = ROTATE_KEYS[e.key.toLowerCase()];
    if (!dir || isEditableTarget(e.target)) return;
    if (e.key.startsWith('Arrow')) e.preventDefault(); // don't scroll the page
    pressedRotateDirs.add(dir);
  });
  window.addEventListener('keyup', (e) => {
    const dir = ROTATE_KEYS[e.key.toLowerCase()];
    if (dir) pressedRotateDirs.delete(dir);
  });
  // Losing window focus mid-press (alt-tab etc.) would otherwise leave a
  // direction "stuck" with no matching keyup ever arriving.
  window.addEventListener('blur', () => pressedRotateDirs.clear());

  canvas.addEventListener('mousemove', (e) => {
    lastAimInput = 'mouse';
    pointer.x = e.clientX;
    pointer.y = e.clientY;
    localTank.turretAngle = Math.atan2(e.clientY - ch / 2, e.clientX - cw / 2);
  });
  canvas.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    lastAimInput = 'mouse';
    const w = screenToWorld(e.clientX, e.clientY);
    trySendShoot(w.x, w.y);
  });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
}

// Touch controls: touching down alone does NOT steer, it just shows a
// direction arrow next to the tank. Only once you
// hold AND drag does the arrow (and the actual heading) start following the
// drag direction, relative to where the touch started (not the raw screen
// position). Firing is a double-tap (two touchstarts close in time/space).
const DOUBLE_TAP_MS = 350;
const DOUBLE_TAP_DIST = 40;
const TOUCH_STEER_DEADZONE = 14; // px of drag before the arrow starts tracking
let lastTapTime = 0;
let lastTapX = 0;
let lastTapY = 0;

let touchSteerActive = false;
let touchAnchorX = 0;
let touchAnchorY = 0;
let touchArrowAngle = 0; // the actual steering target while dragging
let touchArrowDisplayAngle = 0; // eased copy, for a smooth arrow animation
let touchArrowAppearAt = 0;

{
  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault();
    lastAimInput = 'touch';
    const t = e.changedTouches[0];

    const now = Date.now();
    const dt = now - lastTapTime;
    const dist = Math.hypot(t.clientX - lastTapX, t.clientY - lastTapY);
    if (dt < DOUBLE_TAP_MS && dist < DOUBLE_TAP_DIST) {
      // Double-tap: fire only (turret aims via trySendShoot) - never touches
      // steering, so it can't turn the hull.
      const w = screenToWorld(t.clientX, t.clientY);
      trySendShoot(w.x, w.y);
      lastTapTime = 0; // consume, so a fast triple-tap doesn't re-trigger
      touchSteerActive = false;
    } else {
      lastTapTime = now;
      lastTapX = t.clientX;
      lastTapY = t.clientY;

      touchSteerActive = true;
      touchAnchorX = t.clientX;
      touchAnchorY = t.clientY;
      touchArrowAngle = localTank.angle;
      touchArrowDisplayAngle = localTank.angle;
      touchArrowAppearAt = now;
    }
  }, { passive: false });

  canvas.addEventListener('touchmove', (e) => {
    e.preventDefault();
    if (!touchSteerActive) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchAnchorX;
    const dy = t.clientY - touchAnchorY;
    if (Math.hypot(dx, dy) > TOUCH_STEER_DEADZONE) {
      touchArrowAngle = Math.atan2(dy, dx);
      localTank.targetAngle = touchArrowAngle;
    }
  }, { passive: false });

  canvas.addEventListener('touchend', () => { touchSteerActive = false; });
  canvas.addEventListener('touchcancel', () => { touchSteerActive = false; });
}

// ---------------------------------------------------------------------------
// Menu stats + nickname, persisted in localStorage
// ---------------------------------------------------------------------------
const STORAGE_LAST_RUN = 'panzer_last_run';
const STORAGE_BEST_RUN = 'panzer_best_run';
const STORAGE_NICKNAME = 'panzer_nickname';

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) { return fallback; }
}
function saveJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {}
}

function renderMenuStats() {
  const last = loadJSON(STORAGE_LAST_RUN, { kills: 0, shots: 0 });
  const best = loadJSON(STORAGE_BEST_RUN, { score: 0, shots: 0 });
  lastKillsEl.textContent = last.kills;
  lastShotsEl.textContent = last.shots;
  bestScoreEl.textContent = best.score;
  bestShotsEl.textContent = best.shots;
}
renderMenuStats();
nicknameInput.value = loadJSON(STORAGE_NICKNAME, '');

function recordRunStats(score, kills, shots) {
  saveJSON(STORAGE_LAST_RUN, { kills, shots });
  const best = loadJSON(STORAGE_BEST_RUN, { score: -1, shots: 0 });
  if (score > best.score) saveJSON(STORAGE_BEST_RUN, { score, shots });
  renderMenuStats();
}

// ---------------------------------------------------------------------------
// Start / respawn flow
// ---------------------------------------------------------------------------
function setPlayButtonLoading(loading) {
  playBtn.classList.toggle('loading', loading);
  playBtn.disabled = loading;
}

function doPlay() {
  myName = nicknameInput.value.trim();
  saveJSON(STORAGE_NICKNAME, myName);
  setPlayButtonLoading(true);
  connectAndJoin(myName);
}
playBtn.addEventListener('click', doPlay);
nicknameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doPlay(); });

// After death: no button, just a short auto-return to the menu (game-over
// stinger + a slight camera zoom-out carry the moment instead of a timer UI).
const DEATH_AUTO_RETURN_MS = 3000;
let deathReturnTimer = null;

function returnToMenu() {
  postDeathActive = false;
  deathScreen.classList.add('screen-off');
  startScreen.classList.remove('screen-off');
  zoomTargetMultiplier = 1;
  zoomTarget = BASE_ZOOM;
  SoundManager.playLoop('menuLoop', { volume: 0.35 });
  fetchHallOfFame(); // in case this run's score just made the list
}

function startDeathCountdown() {
  clearTimeout(deathReturnTimer);
  zoomTargetMultiplier = 0.88;
  zoomTarget = BASE_ZOOM * zoomTargetMultiplier;
  deathReturnTimer = setTimeout(returnToMenu, DEATH_AUTO_RETURN_MS);
}

// ---------------------------------------------------------------------------
// Local prediction (own tank moves smoothly at 60fps between 30Hz snapshots)
// ---------------------------------------------------------------------------
let lastPredictAt = performance.now();
function updateLocalPrediction(now) {
  const dt = Math.min(0.05, (now - lastPredictAt) / 1000);
  lastPredictAt = now;

  // Ease ZOOM toward its target regardless of gameActive, so the death
  // zoom-out keeps animating (and holds) while the death screen is up.
  if (ZOOM !== zoomTarget) {
    const diff = zoomTarget - ZOOM;
    ZOOM = Math.abs(diff) < 0.001 ? zoomTarget : ZOOM + diff * Math.min(1, dt * 1.8);
  }

  if (!gameActive) return;

  // Hull only turns while mobile - frozen (post-shot) tanks keep their
  // heading exactly as it was, matching the server's authoritative rule.
  if (Date.now() >= localTank.stoppedUntil) {
    // Desktop A/D (or left/right arrows): push the target angle further out
    // at exactly the hull's own turn rate every frame, for as long as the
    // key is held - the turn-toward-target step right below then tracks it
    // 1:1, reading as continuous rotation instead of turning toward a fixed
    // compass point. sendInput() (setInterval, elsewhere) relays the
    // updated targetAngle to the server every 50ms, which turns the
    // authoritative angle toward it at the same rate, so the server keeps
    // rotating in step with what's predicted here.
    if (pressedRotateDirs.has('left')) localTank.targetAngle = normalizeAngle(localTank.targetAngle - cfg.turnRate * dt);
    else if (pressedRotateDirs.has('right')) localTank.targetAngle = normalizeAngle(localTank.targetAngle + cfg.turnRate * dt);

    const diff = normalizeAngle(localTank.targetAngle - localTank.angle);
    const maxTurn = cfg.turnRate * dt;
    if (Math.abs(diff) <= maxTurn) localTank.angle = localTank.targetAngle;
    else localTank.angle += Math.sign(diff) * maxTurn;
    localTank.angle = normalizeAngle(localTank.angle);

    localTank.x += Math.cos(localTank.angle) * cfg.speed * dt;
    localTank.y += Math.sin(localTank.angle) * cfg.speed * dt;
  }
  // No clamping here: driving past the world edge is a valid (fatal) move.
  // The server is authoritative and will send 'dead' within one tick.

  camera.x = localTank.x;
  camera.y = localTank.y;

  // Ease the displayed touch-steer arrow toward its target angle quickly
  // (much faster than the hull itself turns), so it visibly "leads" the
  // tank and reacts snappily to dragging.
  if (touchSteerActive) {
    const diff = normalizeAngle(touchArrowAngle - touchArrowDisplayAngle);
    touchArrowDisplayAngle = normalizeAngle(touchArrowDisplayAngle + diff * Math.min(1, dt * 14));
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------
function hexPoint(cx, cy, size, i) {
  const a = (Math.PI / 180) * (60 * i);
  return [cx + size * Math.cos(a), cy + size * Math.sin(a)];
}

// Paints the hex-line grid for the world rect [originWX, originWX+pixelW/zoom)
// x [originWY, originWY+pixelH/zoom) into targetCtx, with (originWX,originWY)
// at targetCtx's local (0,0). Called by renderHexBuffer() below to fill the
// scrolling buffer, not directly from the render loop.
function paintHexTile(targetCtx, originWX, originWY, pixelW, pixelH, zoom, theme) {
  const size = 42;
  const hexW = size * 2;
  const hexH = Math.sqrt(3) * size;
  const horizStep = hexW * 0.75;
  const vertStep = hexH;

  const left = originWX - hexW;
  const right = originWX + pixelW / zoom + hexW;
  const top = originWY - hexH;
  const bottom = originWY + pixelH / zoom + hexH;

  const colStart = Math.floor(left / horizStep);
  const colEnd = Math.ceil(right / horizStep);

  targetCtx.lineWidth = 1;
  targetCtx.beginPath();
  for (let col = colStart; col <= colEnd; col++) {
    const wx = col * horizStep;
    const colOffset = (Math.abs(col) % 2 !== 0) ? vertStep / 2 : 0;
    const rowStart = Math.floor((top - colOffset) / vertStep);
    const rowEnd = Math.ceil((bottom - colOffset) / vertStep);
    for (let row = rowStart; row <= rowEnd; row++) {
      const wy = row * vertStep + colOffset;
      const sx = (wx - originWX) * zoom;
      const sy = (wy - originWY) * zoom;
      for (let i = 0; i < 6; i++) {
        const [px, py] = hexPoint(sx, sy, size * zoom, i);
        if (i === 0) targetCtx.moveTo(px, py); else targetCtx.lineTo(px, py);
      }
      targetCtx.closePath();
    }
  }
  targetCtx.strokeStyle = THEME_COLORS[theme].hexStroke;
  targetCtx.stroke();
}

// The hex grid only moves because the camera pans (the tank drives forward
// continuously) - the geometry itself never changes, so recomputing >1000
// hexagon paths every single frame was pure waste. Instead, render the grid
// once into an offscreen buffer sized generously larger than the viewport,
// then just blit (drawImage) that buffer at an offset each frame - only
// repainting the buffer when the camera has drifted far enough to run out of
// margin, or zoom/theme changed.
let hexBuffer = null; // { canvasEl, originWX, originWY, zoom, theme }
const HEX_BUFFER_MARGIN = 400; // px of extra buffer around the viewport

function renderHexBuffer() {
  const w = cw + HEX_BUFFER_MARGIN * 2;
  const h = ch + HEX_BUFFER_MARGIN * 2;
  if (!hexBuffer || hexBuffer.canvasEl.width !== w || hexBuffer.canvasEl.height !== h) {
    const canvasEl = document.createElement('canvas');
    canvasEl.width = w;
    canvasEl.height = h;
    hexBuffer = { canvasEl };
  }
  const originWX = camera.x - (cw / 2 + HEX_BUFFER_MARGIN) / ZOOM;
  const originWY = camera.y - (ch / 2 + HEX_BUFFER_MARGIN) / ZOOM;
  const bctx = hexBuffer.canvasEl.getContext('2d');
  bctx.clearRect(0, 0, hexBuffer.canvasEl.width, hexBuffer.canvasEl.height);
  paintHexTile(bctx, originWX, originWY, hexBuffer.canvasEl.width, hexBuffer.canvasEl.height, ZOOM, currentTheme);
  hexBuffer.originWX = originWX;
  hexBuffer.originWY = originWY;
  hexBuffer.zoom = ZOOM;
  hexBuffer.theme = currentTheme;
}

function hexBufferCoversViewport() {
  if (!hexBuffer || hexBuffer.zoom !== ZOOM || hexBuffer.theme !== currentTheme) return false;
  const SAFETY = 50; // px of buffer margin to keep in reserve before a repaint
  const topLeft = worldToScreen(hexBuffer.originWX, hexBuffer.originWY);
  if (topLeft.x > -SAFETY || topLeft.y > -SAFETY) return false;
  if (topLeft.x + hexBuffer.canvasEl.width < cw + SAFETY) return false;
  if (topLeft.y + hexBuffer.canvasEl.height < ch + SAFETY) return false;
  return true;
}

function drawHexGrid() {
  if (!hexBufferCoversViewport()) renderHexBuffer();
  const { x, y } = worldToScreen(hexBuffer.originWX, hexBuffer.originWY);
  ctx.drawImage(hexBuffer.canvasEl, x, y);
}

function worldToScreen(x, y) {
  return { x: (x - camera.x) * ZOOM + cw / 2, y: (y - camera.y) * ZOOM + ch / 2 };
}

// The vignette's shape is fixed relative to the world circle - only its
// screen position (which pans with the camera every frame) and its radius
// (which only changes when ZOOM does, i.e. essentially never outside the
// death zoom-out transition) actually change. Cache the gradient in
// origin-centered local space, keyed by radius, and translate() to place it.
let worldBoundsGradCache = null; // { grad, r }
function getWorldBoundsGradient(r) {
  if (!worldBoundsGradCache || worldBoundsGradCache.r !== r) {
    const grad = ctx.createRadialGradient(0, 0, r * 0.85, 0, 0, r * 1.5);
    grad.addColorStop(0, 'rgba(120,10,10,0.12)');
    grad.addColorStop(1, 'rgba(120,10,10,0.85)');
    worldBoundsGradCache = { grad, r };
  }
  return worldBoundsGradCache.grad;
}

function drawWorldBounds() {
  // The playable map is a circle (radius = worldSize/2, centered on the
  // world) - everything outside it is a red hazard zone.
  const worldCenter = cfg.worldSize / 2;
  const { x: cx, y: cy } = worldToScreen(worldCenter, worldCenter);
  const r = (cfg.worldSize / 2) * ZOOM;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, cw, ch);
  ctx.moveTo(cx + r, cy);
  ctx.arc(cx, cy, r, 0, Math.PI * 2, true); // opposite winding -> a hole
  ctx.closePath();
  ctx.clip('evenodd');

  ctx.translate(cx, cy);
  ctx.fillStyle = getWorldBoundsGradient(r);
  ctx.fillRect(-cx, -cy, cw, ch);
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,70,70,0.7)';
  ctx.lineWidth = Math.max(2, 4 * ZOOM);
  ctx.stroke();
  ctx.restore();
}

// Memoized: each tank's color is stable for its whole lifetime, but this ran
// unmemoized several times per tank per frame - cache by (hex, amt) so the
// hex-parsing/bit-math only happens once per color/shade combo ever seen.
const shadeColorCache = new Map();
function shadeColor(hex, amt) {
  const key = hex + '_' + amt;
  const cached = shadeColorCache.get(key);
  if (cached) return cached;
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) + amt;
  let g = ((num >> 8) & 0xff) + amt;
  let b = (num & 0xff) + amt;
  r = clamp(r, 0, 255); g = clamp(g, 0, 255); b = clamp(b, 0, 255);
  const result = `rgb(${r},${g},${b})`;
  shadeColorCache.set(key, result);
  return result;
}

function drawTouchSteerArrow(color) {
  if (!touchSteerActive) return;
  const age = Date.now() - touchArrowAppearAt;
  const popIn = Math.min(1, age / 150); // quick pop-in when the touch starts
  const pulse = 1 + Math.sin(Date.now() / 220) * 0.06; // gentle "alive" breathing
  const scale = popIn * pulse * ZOOM;

  const dist = 46 * ZOOM;
  const cx = cw / 2, cy = ch / 2; // local tank is always screen-centered
  const ax = cx + Math.cos(touchArrowDisplayAngle) * dist;
  const ay = cy + Math.sin(touchArrowDisplayAngle) * dist;

  ctx.save();
  ctx.translate(ax, ay);
  ctx.rotate(touchArrowDisplayAngle);
  ctx.scale(scale, scale);
  ctx.globalAlpha = 0.4 + 0.5 * popIn;

  ctx.beginPath();
  ctx.moveTo(15, 0);
  ctx.lineTo(-8, -10);
  ctx.lineTo(-2, 0);
  ctx.lineTo(-8, 10);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.fill();
  ctx.restore();
}

// Canvas text has no CSS text-overflow equivalent - shrink `text` character
// by character (binary search) until it plus an ellipsis fits `maxWidth`
// under ctx's *currently set* font, so nametags stay readable over the tank
// instead of sprawling across the battlefield for long nicknames.
function truncateForCanvas(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + '…').width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + '…';
}

function drawTank(x, y, angle, turretAngle, color, name, isMe, stopped, id) {
  const { x: sx, y: sy } = worldToScreen(x, y);
  if (sx < -60 || sx > cw + 60 || sy < -60 || sy > ch + 60) return;

  ctx.save();
  ctx.translate(sx, sy);

  // Hull + turret: both baked Rookie sprite layers (public/tank-sprites.js),
  // each independently matched to the nearest of its own 32 baked headings
  // x the matching TANK_COLORS entry - drawImage() only, no live per-frame
  // color math (see wild-percolating-breeze.md). TankSprites.drawTankSprite()
  // draws both, compositing the turret on top of the hull at the hull's own
  // baked mount point for the current heading, plus (PHASE 4) a brief
  // recoil kick on the turret layer keyed off `stopped`'s rising edge - `id`
  // gives each tank its own independent recoil timer.
  TankSprites.drawTankSprite(ctx, 0, 0, angle, turretAngle, colorToIndex(color), {
    color, zoom: ZOOM, stopped, id,
  });

  ctx.restore();

  if (name) {
    ctx.save();
    ctx.font = isMe ? 'bold 13px Trebuchet MS' : '12px Trebuchet MS';
    ctx.fillStyle = isMe ? '#ffe36e' : '#ffffff';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 4;
    ctx.fillText(truncateForCanvas(ctx, name, 140), sx, sy - (24 * ZOOM + 12));
    ctx.restore();
  }
}

function projectilePoint(p, t) {
  const dist = Math.hypot(p.tx - p.sx, p.ty - p.sy);
  const arcH = Math.sin(clamp(t, 0, 1) * Math.PI) * (26 + dist * 0.08) * ZOOM;
  const wx = lerp(p.sx, p.tx, clamp(t, 0, 1));
  const wy = lerp(p.sy, p.ty, clamp(t, 0, 1));
  const ground = worldToScreen(wx, wy);
  return { gx: ground.x, gy: ground.y, arcH };
}

// The cannonball's shading is a fixed-shape gradient (same relative highlight
// offset/color stops always) that only scales with r (effectively only with
// ZOOM, which is near-constant) - cache it in local space keyed by r instead
// of building a fresh gradient for every projectile on every frame.
let projectileBallGradCache = null; // { grad, r }
function getProjectileBallGradient(r) {
  if (!projectileBallGradCache || projectileBallGradCache.r !== r) {
    const grad = ctx.createRadialGradient(-r * 0.35, -r * 0.35, r * 0.15, 0, 0, r);
    grad.addColorStop(0, '#8d8d8d');
    grad.addColorStop(0.55, '#2b2b2b');
    grad.addColorStop(1, '#0a0a0a');
    projectileBallGradCache = { grad, r };
  }
  return projectileBallGradCache.grad;
}

function drawProjectiles() {
  const estServerNow = Date.now() + serverOffset;
  const r = 11 * ZOOM;

  for (const p of latestState.projectiles) {
    const t = clamp((estServerNow - p.startTime) / p.flightTime, 0, 1);
    const { gx, gy, arcH } = projectilePoint(p, t);
    // Same off-screen culling margin as drawTank - projectiles/explosions
    // were previously drawn unconditionally even miles outside the viewport.
    if (gx < -60 || gx > cw + 60 || gy < -60 || gy > ch + 60) continue;

    // ground shadow - grows/darkens as the ball gets higher, marks the impact spot
    ctx.beginPath();
    ctx.ellipse(gx, gy, (9 + arcH * 0.12) * ZOOM, (4.5 + arcH * 0.06) * ZOOM, 0, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(0,0,0,${0.4 * (1 - t * 0.25)})`;
    ctx.fill();

    // short fading trail behind the ball so its motion reads clearly
    for (let i = 3; i >= 1; i--) {
      const trailT = t - i * 0.045;
      if (trailT <= 0) continue;
      const tp = projectilePoint(p, trailT);
      ctx.beginPath();
      ctx.arc(tp.gx, tp.gy - tp.arcH, r * (0.75 - i * 0.14), 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,225,150,${0.16 - i * 0.03})`;
      ctx.fill();
    }

    // the cannonball itself: bright rim so it pops against both the dark
    // grid and colored tanks, plus a highlight to read as a sphere.
    const by = gy - arcH;
    ctx.save();
    ctx.translate(gx, by);
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fillStyle = getProjectileBallGradient(r);
    ctx.fill();
    ctx.lineWidth = Math.max(1.5, 2 * ZOOM);
    ctx.strokeStyle = 'rgba(255,235,180,0.9)';
    ctx.stroke();
    ctx.restore();
  }
}

// Pre-rendered once at full opacity - each explosion instance just scales
// and alpha-fades this sprite via drawImage()/globalAlpha instead of a fresh
// createRadialGradient() per explosion per frame. globalAlpha multiplies the
// sprite's own per-pixel alpha uniformly, which is mathematically identical
// to the original per-stop `* (1 - t)` scaling below.
const EXPLOSION_SPRITE_SIZE = 128;
const explosionSprite = (() => {
  const c = document.createElement('canvas');
  c.width = c.height = EXPLOSION_SPRITE_SIZE;
  const sctx = c.getContext('2d');
  const mid = EXPLOSION_SPRITE_SIZE / 2;
  const grad = sctx.createRadialGradient(mid, mid, 0, mid, mid, mid);
  grad.addColorStop(0, 'rgba(255,230,150,0.9)');
  grad.addColorStop(0.5, 'rgba(255,140,40,0.7)');
  grad.addColorStop(1, 'rgba(255,60,20,0)');
  sctx.fillStyle = grad;
  sctx.fillRect(0, 0, EXPLOSION_SPRITE_SIZE, EXPLOSION_SPRITE_SIZE);
  return c;
})();

function drawExplosions() {
  const now = Date.now();
  for (let i = explosions.length - 1; i >= 0; i--) {
    const ex = explosions[i];
    const age = now - ex.startedAt;
    const dur = ex.big ? 500 : 350;
    if (age > dur) { explosions.splice(i, 1); continue; }
    const t = age / dur;
    const maxR = (ex.big ? 46 : 30) * ZOOM;
    const r = maxR * (0.3 + t * 0.7);
    const { x, y } = worldToScreen(ex.x, ex.y);
    if (x < -60 - r || x > cw + 60 + r || y < -60 - r || y > ch + 60 + r) continue;
    ctx.save();
    ctx.globalAlpha = 1 - t;
    ctx.drawImage(explosionSprite, x - r, y - r, r * 2, r * 2);
    ctx.restore();
  }
}

function drawTargetMarkers() {
  const now = Date.now();
  const dur = 500;
  for (let i = targetMarkers.length - 1; i >= 0; i--) {
    const m = targetMarkers[i];
    const age = now - m.startedAt;
    if (age > dur) { targetMarkers.splice(i, 1); continue; }
    const t = age / dur;
    const ease = 1 - (1 - t) * (1 - t); // ease-out: fast start, slows down
    const r = (5 + ease * 22) * ZOOM;
    const alpha = 1 - t;
    const { x, y } = worldToScreen(m.x, m.y);

    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.lineWidth = Math.max(1.5, 3 * ZOOM);
    ctx.strokeStyle = `rgba(255,60,60,${0.85 * alpha})`;
    ctx.shadowColor = 'rgba(255,60,60,0.9)';
    ctx.shadowBlur = 10 * ZOOM;
    ctx.stroke();
    ctx.restore();
  }
}

let minimapSizedW = -1;
let minimapSizedH = -1;
function drawMinimap() {
  const w = minimap.clientWidth || 110;
  const h = minimap.clientHeight || 110;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  // canvas.width/height assignment always clears the backing store and
  // resets all context state (transform included) - only pay that cost when
  // the on-screen size actually changed, not on every one of the 60 frames/s
  // this is called from.
  if (w !== minimapSizedW || h !== minimapSizedH) {
    minimapCtx.canvas.width = w * dpr;
    minimapCtx.canvas.height = h * dpr;
    minimapCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    minimapSizedW = w;
    minimapSizedH = h;
  }
  minimapCtx.clearRect(0, 0, w, h);

  minimapCtx.save();
  minimapCtx.beginPath();
  minimapCtx.arc(w / 2, h / 2, w / 2 - 2, 0, Math.PI * 2);
  minimapCtx.clip();
  minimapCtx.fillStyle = 'rgba(255,255,255,0.03)';
  minimapCtx.fillRect(0, 0, w, h);

  for (const p of latestState.players) {
    const mx = (p.x / cfg.worldSize) * w;
    const my = (p.y / cfg.worldSize) * h;
    minimapCtx.beginPath();
    const isMe = p.id === myId;
    minimapCtx.arc(mx, my, isMe ? 1 : 0.75, 0, Math.PI * 2);
    minimapCtx.fillStyle = isMe ? '#ffe36e' : 'rgba(200,200,210,0.75)';
    minimapCtx.fill();
  }
  minimapCtx.restore();
}

function drawLeaderboard() {
  const sorted = [...latestState.players].sort((a, b) => b.score - a.score).slice(0, 10);
  lbList.innerHTML = '';
  sorted.forEach((p, i) => {
    const info = roster.get(p.id);
    const name = info ? info.name : '';
    const color = info ? info.color : '#888';
    const li = document.createElement('li');
    if (p.id === myId) li.className = 'me';
    const nameSpan = document.createElement('span');
    nameSpan.className = 'lb-name';
    nameSpan.textContent = name ? `${i + 1}. ${name}` : `${i + 1}.`;
    nameSpan.style.color = color;
    const scoreSpan = document.createElement('span');
    scoreSpan.className = 'lb-score';
    scoreSpan.textContent = p.score;
    scoreSpan.style.color = color;
    li.appendChild(nameSpan);
    li.appendChild(scoreSpan);
    lbList.appendChild(li);
  });
}

function getRenderPos(p) {
  if (p.stopped) return { x: p.x, y: p.y };
  const elapsed = Math.min(0.15, (Date.now() - latestRecvAt) / 1000);
  return {
    x: p.x + Math.cos(p.angle) * cfg.speed * elapsed,
    y: p.y + Math.sin(p.angle) * cfg.speed * elapsed,
  };
}

function getTurretAngle(p) {
  // While frozen after a shot, aim the turret at that shot's exact target
  // (there's at most one in-flight shell owned by a tank while it's stopped,
  // since firing again is blocked until the freeze ends). Otherwise the
  // turret just follows the hull.
  if (p.stopped) {
    const pr = latestState.projectiles.find((pr) => pr.ownerId === p.id);
    if (pr) return Math.atan2(pr.ty - pr.sy, pr.tx - pr.sx);
  }
  return p.angle;
}

function drawStatbar() {
  const me = latestState.players.find((p) => p.id === myId);
  statScore.textContent = me ? me.score : 0;

  const remaining = localTank.stoppedUntil - Date.now();
  if (remaining > 0) {
    cooldownRing.classList.remove('hidden');
    const frac = clamp(remaining / cfg.shotStopMs, 0, 1);
    ringFg.style.strokeDashoffset = String(113 * frac);
  } else {
    cooldownRing.classList.add('hidden');
  }
}

function draw() {
  ctx.clearRect(0, 0, cw, ch);
  ctx.fillStyle = THEME_COLORS[currentTheme].canvasBg;
  ctx.fillRect(0, 0, cw, ch);

  drawHexGrid();
  drawWorldBounds();

  for (const p of latestState.players) {
    if (p.id === myId) continue;
    const rp = getRenderPos(p);
    const info = roster.get(p.id);
    drawTank(rp.x, rp.y, p.angle, getTurretAngle(p), info ? info.color : '#888', info ? info.name : '', false, p.stopped, p.id);
  }

  if (gameActive) {
    const myInfo = roster.get(myId);
    const myColor = myInfo ? myInfo.color : '#3ddc55';
    const stopped = Date.now() < localTank.stoppedUntil;
    // Mouse aim: the turret continuously follows the pointer, freeze or not.
    // Touch aim: it only deviates from the hull to hold the exact aim of the
    // last shot while frozen - otherwise it just faces the drive direction.
    const shownTurretAngle = lastAimInput === 'touch'
      ? (stopped ? localTank.turretAngle : localTank.angle)
      : localTank.turretAngle;
    drawTank(
      localTank.x, localTank.y, localTank.angle,
      shownTurretAngle,
      myColor,
      myName, true, stopped, myId
    );
    drawTouchSteerArrow(myColor);
  }

  drawProjectiles();
  drawExplosions();
  drawTargetMarkers();

  if (gameActive || hud.classList.contains('screen-off') === false) {
    drawMinimap();
    drawStatbar();
  }
}

function frame(now) {
  requestAnimationFrame(frame);
  updateLocalPrediction(now);
  draw();
}
requestAnimationFrame(frame);
